import type { RelayStatus } from "../protocol/event-types";
import {
  compareStreamIds,
  isStreamEvent,
  parseSseBuffer,
  type StreamEvent,
} from "../protocol/frames";
import {
  buildStreamUrl,
  channelsToRequest,
  nextReconnectDelayMs,
  type TokenOutcome,
} from "./connection-plan";

const FIRST_BYTE_TIMEOUT_MS = 10_000;
const IDLE_TIMEOUT_MS = 75_000;
const SIGNED_OUT_POLL_MS = 5_000;
/**
 * A page hidden this long closes its stream; showing it again reopens the stream from the last
 * event seen. An open stream holds a relay function (and the user's presence) for as long as it
 * lives, so a background tab left open overnight cost as much as one in use.
 */
const HIDDEN_CLOSE_AFTER_MS = 5 * 60_000;

type SystemFrameType = "connected" | "reconnect" | "error" | "ping";

export type RelayConnection = {
  /**
   * The channels to stream. `liveWhileHidden` keeps the stream open on a hidden page, for a
   * subscription waiting on the outcome of work the page started (an upload being processed).
   */
  setChannels: (
    channels: string[],
    options?: { liveWhileHidden?: boolean },
  ) => void;
  /** Whether the page is hidden (a background tab, a minimized window). */
  setPageHidden: (hidden: boolean) => void;
  close: () => void;
};

export function createRelayConnection(config: {
  url: string;
  getToken: () => Promise<string | null>;
  /** Channels the relay serves without a token; a signed-out viewer connects to these alone. */
  isPublicChannel?: (channel: string) => boolean;
  onEvent: (event: StreamEvent) => void;
  onStatus: (status: RelayStatus) => void;
  fetch?: typeof fetch; // test seam
}): RelayConnection {
  const doFetch = config.fetch ?? fetch;
  let channels: string[] = [];
  const acks = new Map<string, string>();
  const since = new Map<string, number>();
  let attempt = 0;
  let status: RelayStatus = "disconnected";
  // One generation per connect(). Every await and timer checks it, so a superseded attempt never
  // touches status, timers or the retry schedule.
  let generation = 0;
  let current: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let pageHidden = false;
  let liveWhileHidden = false;
  // True from the hidden-page close until the page is shown (or a subscription needs it live).
  let closedWhileHidden = false;
  let hiddenCloseTimer: ReturnType<typeof setTimeout> | undefined;

  const setStatus = (next: RelayStatus) => {
    if (next === status) return;
    status = next;
    config.onStatus(next);
  };

  function stop() {
    generation++;
    current?.abort();
    current = null;
    clearTimeout(retryTimer);
  }

  function schedule(delayMs: number) {
    clearTimeout(retryTimer);
    retryTimer = setTimeout(() => void connect(), delayMs);
  }

  function closeWhileHidden() {
    hiddenCloseTimer = undefined;
    closedWhileHidden = true;
    attempt = 0; // reopening is a fresh start, not the next retry
    stop();
    setStatus("disconnected");
  }

  /**
   * Starts or cancels the hidden-page countdown after the page's visibility or the subscriptions'
   * needs changed. Returns whether it reopened a stream it had closed.
   */
  function planHiddenClose(): boolean {
    if (pageHidden && !liveWhileHidden) {
      if (!closedWhileHidden && hiddenCloseTimer === undefined) {
        hiddenCloseTimer = setTimeout(closeWhileHidden, HIDDEN_CLOSE_AFTER_MS);
      }
      return false;
    }
    clearTimeout(hiddenCloseTimer);
    hiddenCloseTimer = undefined;
    if (!closedWhileHidden) return false;
    closedWhileHidden = false;
    void connect(); // acks and since survived the close, so the relay replays what was missed
    return true;
  }

  async function connect() {
    stop();
    const gen = generation;
    const isCurrent = () => gen === generation;
    if (closedWhileHidden) return; // the stream reopens when the page is shown
    if (channels.length === 0) return setStatus("disconnected");

    const token = await config.getToken().then(
      (value): TokenOutcome =>
        value === null
          ? { kind: "signed-out" }
          : { kind: "token", token: value },
      (): TokenOutcome => ({ kind: "failed" }),
    );
    if (!isCurrent()) return;
    const requested = channelsToRequest(
      channels,
      token,
      (channel) => config.isPublicChannel?.(channel) ?? false,
    );
    if (requested.length === 0) {
      // Signed out with nothing public, or no token to open a private channel with: no request,
      // no backoff growth, check the token again shortly.
      setStatus("disconnected");
      return schedule(SIGNED_OUT_POLL_MS);
    }

    // During an outage "error" stays until a stream connects, so the upload tracker's one-shot
    // reconcile runs once per outage, not once per retry.
    if (status !== "error") setStatus("connecting");

    const controller = new AbortController();
    current = controller;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    const armIdle = (ms: number) => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => controller.abort(new Error("idle")), ms);
    };
    const fail = () => {
      clearTimeout(idleTimer);
      if (!isCurrent()) return; // intentional abort by setChannels / close / reconnect
      controller.abort();
      setStatus("error");
      schedule(nextReconnectDelayMs(attempt++));
    };

    armIdle(FIRST_BYTE_TIMEOUT_MS); // Rule 21: bound time-to-first-byte and idle gaps, never the stream
    try {
      const response = await doFetch(
        buildStreamUrl(config.url, requested, acks, since),
        {
          headers:
            token.kind === "token"
              ? {
                  Authorization: `Bearer ${token.token}`,
                  Accept: "text/event-stream",
                }
              : { Accept: "text/event-stream" },
          cache: "no-store",
          signal: controller.signal,
        },
      );
      if (!isCurrent()) return;
      if (!response.ok || !response.body) return fail(); // 401/403 included: next attempt gets a fresh token

      const reader = response.body.getReader();
      const decoder = new TextDecoder(); // one per attempt; stream mode keeps split multibyte chars intact
      let text = "";
      for (;;) {
        armIdle(IDLE_TIMEOUT_MS);
        const { value, done } = await reader.read();
        if (!isCurrent()) return;
        if (done) return fail(); // ended without a reconnect frame
        text += decoder.decode(value, { stream: true });
        const { frames, rest } = parseSseBuffer(text);
        text = rest;
        for (const frame of frames) {
          if (isStreamEvent(frame)) {
            const last = acks.get(frame.channel);
            if (last && compareStreamIds(frame.id, last) <= 0) continue; // de-dup by id
            if (!requested.includes(frame.channel)) continue;
            acks.set(frame.channel, frame.id);
            config.onEvent(frame);
            continue;
          }
          const type = (frame as { type?: SystemFrameType }).type;
          if (type === "connected") {
            attempt = 0;
            setStatus("connected");
          } else if (type === "reconnect") {
            clearTimeout(idleTimer);
            attempt = 0;
            return void connect();
          } else if (type === "error") {
            return fail();
          }
        }
      }
    } catch {
      return fail(); // fail() ignores superseded attempts, so an AbortError from stop() is a no-op
    } finally {
      clearTimeout(idleTimer);
    }
  }

  return {
    setChannels(next, options) {
      const unique = [...new Set(next)].sort();
      for (const channel of [...acks.keys()])
        if (!unique.includes(channel)) acks.delete(channel);
      for (const channel of [...since.keys()])
        if (!unique.includes(channel)) since.delete(channel);
      for (const channel of unique)
        if (!since.has(channel)) since.set(channel, Date.now());
      const changed = unique.join(",") !== channels.join(",");
      channels = unique;
      liveWhileHidden = options?.liveWhileHidden ?? false;
      const reopened = planHiddenClose();
      if (changed && !reopened) void connect();
    },
    setPageHidden(hidden) {
      pageHidden = hidden;
      planHiddenClose();
    },
    close() {
      clearTimeout(hiddenCloseTimer);
      hiddenCloseTimer = undefined;
      stop();
      // A replacement connection starts at "disconnected" and never re-reports it, so reset here.
      setStatus("disconnected");
    },
  };
}
